// components/groups/GroupInfoCard.tsx
import React, { useState, useEffect } from "react";
import { showSuccess, showError } from "toast/toast";

import { getAuthCookie } from "../../utils/authCookies";

export type Groups = {
  _id?: string;
  group_id?: string;              // some responses use group_id
  group_name: string;
  status: "Active" | "Inactive" | string;
  total_members: number;
  active_members: number;
};

type Props = { group: Groups; onUpdated: (g: Groups) => void };

const GroupInfoCard: React.FC<Props> = ({ group, onUpdated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(group.group_name);
  const [status, setStatus] = useState(group.status ?? "Active");
  const [saving, setSaving] = useState(false);

  const total = Number(group.total_members ?? 0);
  const active = Number(group.active_members ?? 0);
  const groupId = group.group_id || group._id || "";

  // Sync local state with group prop when it changes
  useEffect(() => {
    if (group.group_name !== undefined && group.group_name !== null) {
      setName(group.group_name);
    }
    if (group.status !== undefined && group.status !== null) {
      setStatus(group.status);
    }
  }, [group.group_name, group.status]);

  const handleUpdate = async () => {
    const token = getAuthCookie("sessionToken");
    if (!token || !groupId) {
      console.error("Missing token or groupId");
      return;
    }
    
    try {
      setSaving(true);
      console.log("Sending PUT request with:", { name, status });
      
      const res = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups/${groupId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name, status }),
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Update failed");
      }
      
      const updated = await res.json();
      
      onUpdated(updated);
      
      showSuccess("Group updated successfully.");
      setIsEditing(false);
    } catch (err) {
      showError("Failed to update group.");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setName(group.group_name);
    setStatus(group.status ?? "Active");
    setIsEditing(false);
  };

  return (
    <div className="card border-0 shadow-lg rounded-4 mb-4 gi-card">
      {/* scoped internal CSS */}
      <style>{`
        .gi-card .gi-header{background:#155dfc;border-radius:1rem 1rem 0 0;padding:.75rem 1rem;}
        .gi-card .gi-chip{width:34px;height:34px;border-radius:10px;background:#447dfd;display:flex;align-items:center;justify-content:center;}
        .gi-card .gi-title{color:#fff;font-weight:700;}
        /* Buttons */
        .gi-card .gi-actions .btn{border-radius:.6rem}
        .gi-card .gi-edit-btn{background:#447dfd;color:#fff;}
        .gi-card .gi-edit-btn:hover{background:#5c8efd;color:#fff;}  /* lighter on hover */
        .gi-card .gi-update{display:inline-flex;align-items:center;gap:.4rem;background:#00a63e;border:0;color:#fff;}
        .gi-card .gi-update:hover{background:#008236;color:#fff;}
        .gi-card .gi-cancel{display:inline-flex;align-items:center;gap:.45rem;border:1px solid rgba(255,255,255,.7);color:#fff;background:transparent;}
        .gi-card .gi-cancel:hover{background:#5c8efd;color:black;}
        /* Status pills */
        .gi-card .gi-status{display:inline-flex;align-items:center;font-weight:500;border-radius:.5rem;padding:.25rem .6rem;}
        .gi-card .gi-status-active{background:#dcfce7;color:#016630;border:1px solid rgba(1,102,48,.18);}
        .gi-card .gi-status-inactive{background:#f1f5f9;color:#475569;border:1px solid rgba(71,85,105,.15);}
        /* Total Members chip */
        .gi-card .gi-metric{display:flex;align-items:center;gap:.75rem}
        .gi-card .gi-chip-blue{
          width:36px;height:36px;background:#EAF2FF;color:#2F6CF5;
          display:flex;align-items:center;justify-content:center;font-size:20px;
        }
        .gi-card .gi-total{font-size:1.9rem;line-height:1;font-weight:600;color:#0f172a}
        .gi-card .gi-active{color:#64748b}
        @media (max-width:576px){ .gi-card .gi-total{font-size:1.6rem} }
      `}</style>

      {/* Header */}
      <div className="card-header gi-header text-white rounded-top-4 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <span className="gi-chip"><i className="ri-settings-3-line text-white fs-6" /></span>
          <h5 className="mb-0 gi-title">Group Information</h5>
        </div>

        {!isEditing ? (
          <button className="btn btn-sm gi-edit-btn fs-13 br-8 fw-medium" onClick={() => setIsEditing(true)}>
            <i className="ri-edit-line me-1" /> Edit
          </button>
        ) : (
          <div className="gi-actions d-flex gap-2">
            <button className="btn btn-sm gi-update fw-medium fs-13" onClick={handleUpdate} disabled={saving}>
              <i className="ri-save-3-line" /> {saving ? "Updating..." : "Update"}
            </button>
            <button className="btn btn-sm gi-cancel fs-13 br-8 fw-medium" onClick={cancelEdit}>
              <i className="ri-close-line" /> Cancel   {/* plain close icon */}
            </button>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="card-body">
        <div className="row g-4">
          {/* Group Name */}
          <div className="col-12 col-lg-6">
            <label className="form-label text-muted"text-muted>
              Group Name{" "}
              <span className={isEditing ? "text-danger" : "text-muted"}>*</span>
            </label>
            {!isEditing ? (
              <div className="fs-5 text-body">{name}</div>
            ) : (
              <input
                className="form-control rounded-3"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter group name"
              />
            )}
          </div>

          {/* Status */}
          <div className="col-12 col-lg-6">
            <label className="form-label text-muted"text-muted>
              Status{" "}
              <span className={isEditing ? "text-danger" : "text-muted"}>*</span>
            </label>

            {!isEditing ? (
              <div>
                <span
                  className={
                    status === "Active"
                      ? "fs-10 fw-bold gi-status gi-status-active"
                      : "fs-10 fw-bold gi-status gi-status-inactive"
                  }
                >
                  {status}
                </span>
              </div>
            ) : (
              <select
                className="form-select rounded-3"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            )}
          </div>

          {/* Total Members */}
          {/* <div className="col-12 col-lg-3">
            <label className="form-label text-muted mb-2">
              Total Members
            </label>
            <div className="gi-metric">
              <span className="gi-chip-blue br-8" aria-hidden="true">
                <i className="ri-group-line"></i>
              </span>
              <div>
                <div className="gi-total">{total}</div>
                <div className="gi-active">
                  {active} active members
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default GroupInfoCard;
